import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Clock, AlertCircle, Check, Info, Trash2, UserPlus, Handshake, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { api, type NotificationDto } from '../services/api';
import { useLedgerStore } from '../hooks/use-ledger-store';
import { useToast } from '../hooks/use-toast';

const PAGE_SIZE = 10;

/**
 * 功能：解析通知 content（可能為 JSON 字串）
 * @param {string} content 原始內容
 * @returns {string} 顯示用文字
 */
function parseContent(content: string): string {
  try {
    const parsed = JSON.parse(content);
    return typeof parsed === 'string' ? parsed : String(parsed);
  } catch {
    return content;
  }
}

/**
 * 功能：依通知類型回傳圖示
 * @param {string} type 通知類型
 * @returns {JSX.Element} 圖示元件
 */
function getTypeIcon(type: string) {
  switch (type) {
    case 'expense_added':
      return <AlertCircle size={18} className="text-secondary" />;
    case 'expense_modified':
      return <Info size={18} className="text-blue-400" />;
    case 'expense_deleted':
      return <Trash2 size={18} className="text-primary" />;
    case 'member_changed':
      return <UserPlus size={18} className="text-purple-400" />;
    case 'settlement':
      return <Handshake size={18} className="text-secondary" />;
    default:
      return <Bell size={18} className="text-muted-foreground" />;
  }
}

/**
 * 功能：系統通知鈴鐺；下拉清單分頁並捲動自動載入，點擊以視窗 Toast 顯示
 * @returns {JSX.Element} 通知下拉選單
 */
export default function NotificationBell() {
  const { refresh, unreadNotificationCount } = useLedgerStore();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [unreadCount, setUnreadCount] = useState(unreadNotificationCount);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    setUnreadCount(unreadNotificationCount);
  }, [unreadNotificationCount]);

  /**
   * 功能：載入一頁通知並合併到清單
   * @param {number} pageOffset 起始 offset
   * @param {boolean} [append=false] 是否附加到既有清單
   * @returns {Promise<void>}
   */
  const loadPage = useCallback(async (pageOffset: number, append = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (append) setIsLoadingMore(true);
    else setIsLoadingPage(true);
    try {
      const resp = await api.notifications.list({ limit: PAGE_SIZE, offset: pageOffset });
      if (!resp) return;
      setItems((prev) => (append ? [...prev, ...resp.items] : resp.items));
      setHasMore(resp.hasMore);
      setOffset(pageOffset + resp.items.length);
      setUnreadCount(resp.unreadCount);
    } catch (err) {
      console.error(err);
      showToast('載入通知失敗', 'error');
    } finally {
      loadingRef.current = false;
      setIsLoadingPage(false);
      setIsLoadingMore(false);
    }
  }, [showToast]);

  const handleOpenChange = async (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) return;

    setItems([]);
    setOffset(0);
    setHasMore(false);

    try {
      loadingRef.current = true;
      setIsLoadingPage(true);
      const resp = await api.notifications.list({ limit: PAGE_SIZE, offset: 0 });
      if (resp) {
        setItems(resp.items);
        setHasMore(resp.hasMore);
        setOffset(resp.items.length);
        setUnreadCount(resp.unreadCount);

        if (resp.unreadCount > 0) {
          await api.notifications.markAllRead();
          setUnreadCount(0);
          setItems((prev) => prev.map((n) => ({ ...n, isRead: 1 })));
          await refresh();
        }
      }
    } catch (err) {
      console.error(err);
      showToast('載入通知失敗', 'error');
    } finally {
      loadingRef.current = false;
      setIsLoadingPage(false);
    }
  };

  /**
   * 功能：標記單則已讀
   * @param {string} id 通知 id
   * @returns {Promise<void>}
   */
  const handleMarkRead = async (id: string) => {
    try {
      await api.notifications.markRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: 1 } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
      await refresh();
    } catch (err) {
      console.error(err);
      showToast('標記已讀失敗', 'error');
    }
  };

  /**
   * 功能：以視窗化 Toast 顯示通知詳情
   * @param {NotificationDto} notif 通知列
   * @returns {void}
   */
  const handleShowNotification = (notif: NotificationDto) => {
    const body = parseContent(notif.content);
    const message = notif.title ? `${notif.title}\n${body}` : body;
    showToast(message, notif.type === 'expense_deleted' ? 'error' : 'info');
    if (!notif.isRead) {
      void handleMarkRead(notif.id);
    }
  };

  /**
   * 功能：下拉捲動接近底部時自動載入下一頁
   * @returns {void}
   */
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || !hasMore || loadingRef.current) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      void loadPage(offset, true);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger
        className="relative group inline-flex items-center justify-center w-9 h-9 rounded-xl hover:bg-white/5 transition-all focus:outline-none"
      >
        <Bell
          size={20}
          className={`${unreadCount > 0 ? 'text-primary' : 'text-muted-foreground'} group-hover:scale-110 transition-transform`}
        />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary border-2 border-background"></span>
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] p-0 bg-background/95 backdrop-blur-3xl border-border rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="p-4 border-b border-border/50 flex items-center justify-between">
          <h3 className="text-xs font-black text-foreground uppercase tracking-widest">系統通知</h3>
          <Badge variant="outline" className="border-primary/20 text-primary font-bold text-[10px]">
            {unreadCount} 則未讀
          </Badge>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-[400px] overflow-y-auto overscroll-contain"
        >
          <div className="divide-y divide-border/10">
            {isLoadingPage && items.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
                <Loader2 size={28} className="animate-spin mb-3" />
                <p className="text-sm font-bold">載入中...</p>
              </div>
            ) : items.length > 0 ? (
              <>
                {items.map((notif) => {
                  const isRead = !!notif.isRead;
                  return (
                    <div
                      key={notif.id}
                      tabIndex={0}
                      onClick={() => handleShowNotification(notif)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleShowNotification(notif);
                        }
                      }}
                      className={`p-4 transition-colors relative group cursor-pointer hover:bg-accent/40 ${
                        isRead ? 'opacity-60 grayscale-[0.5]' : 'bg-primary/5'
                      }`}
                    >
                      <div className="flex gap-4">
                        <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center shrink-0">
                          {getTypeIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-bold truncate pr-2 text-foreground">
                              {notif.title}
                            </span>
                            {!isRead && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 rounded-full hover:bg-secondary/20 hover:text-secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleMarkRead(notif.id);
                                }}
                              >
                                <Check size={14} />
                              </Button>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground font-medium mb-2 leading-relaxed">
                            {parseContent(notif.content)}
                          </p>
                          <div className="flex items-center gap-1.5 text-[8px] text-muted-foreground/60 font-bold uppercase tracking-wider">
                            <Clock size={8} />
                            {new Date(notif.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {isLoadingMore && (
                  <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground text-xs font-bold">
                    <Loader2 size={14} className="animate-spin" />
                    載入更多...
                  </div>
                )}
                {!hasMore && items.length > 0 && (
                  <p className="text-center text-[10px] text-muted-foreground/70 font-medium py-3">
                    已載入全部通知
                  </p>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-12 text-center opacity-40">
                <Bell size={40} className="mb-4 text-muted-foreground" />
                <p className="text-sm font-bold">目前沒有新通知</p>
                <p className="text-[10px] mt-1 font-medium italic">當有帳務異動時會顯示在這裡。</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-3 border-t border-border/50 bg-muted/30">
          <Button
            variant="ghost"
            className="w-full text-[9px] font-black uppercase text-muted-foreground hover:text-primary tracking-[0.2em] h-8"
            onClick={() => setOpen(false)}
          >
            關閉
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
